import { useTranslation } from 'react-i18next';
import {
  Badge, Button, MessageBar, MessageBarBody, Spinner, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow,
  makeStyles, tokens,
} from '@fluentui/react-components';
import { CalendarSyncRegular } from '@fluentui/react-icons';
import { useIsStateTaxAdmin } from '../../services/stateTaxClientsApi';
import { useWindowSweepPreview, type WindowSweepItem } from '../../services/complianceApi';
import { apiErrorMessage } from '../../lib/apiErrorMessage';

// The nightly window sweep's DRY RUN, as a button (Christian, 2026-10-27: "build the admin-only sweep 'preview' button … so the
// next dry run is a click, not a workaround"). ADMINS ONLY. It calls GET state-tax/obligations/window-sweep/preview, which plans
// the sweep in both modes (Cosmos only, and with Dataverse) and WRITES NOTHING. The card never decides anything: it shows the
// server's plan, names every task that would newly be cancelled or kept, and says plainly when the Dataverse read failed.

const useStyles = makeStyles({
  section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
  },
  summary: { color: tokens.colorNeutralForeground2 },
  row: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  subTitle: { fontWeight: tokens.fontWeightSemibold },
  tableCard: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    overflowX: 'auto',
  },
  muted: { color: tokens.colorNeutralForeground3 },
});

export default function SweepPreviewCard() {
  const styles = useStyles();
  const { t } = useTranslation();
  const { data: isAdmin = false } = useIsStateTaxAdmin();
  const { data, isFetching, isError, error, refetch } = useWindowSweepPreview();

  if (!isAdmin) return null;

  const itemsTable = (items: WindowSweepItem[], testId: string) => (
    <div className={styles.tableCard}>
      <Table size="small" aria-label={t(`managementPage.sweepPreview.${testId}`)} data-testid={`sweep-preview-${testId}`}>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>{t('managementPage.sweepPreview.columns.client')}</TableHeaderCell>
            <TableHeaderCell>{t('managementPage.sweepPreview.columns.period')}</TableHeaderCell>
            <TableHeaderCell>{t('managementPage.sweepPreview.columns.status')}</TableHeaderCell>
            <TableHeaderCell>{t('managementPage.sweepPreview.columns.why')}</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.taskId}>
              <TableCell>{item.clientName ?? item.clientId}</TableCell>
              <TableCell>{item.periodLabel}</TableCell>
              <TableCell>{item.status}</TableCell>
              {/* The server's own reason (it names the window). Shown as-is, never wrapped in an English preamble. */}
              <TableCell>{item.note}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <section className={styles.section} data-testid="sweep-preview-card">
      <div className={styles.sectionTitle}>
        <CalendarSyncRegular />
        {t('managementPage.sweepPreview.title')}
      </div>
      <span className={styles.summary}>{t('managementPage.sweepPreview.intro')}</span>
      <div className={styles.row}>
        <Button appearance="primary" onClick={() => void refetch()} disabled={isFetching}>
          {t('managementPage.sweepPreview.run')}
        </Button>
        {isFetching && <Spinner size="tiny" label={t('managementPage.sweepPreview.running')} />}
      </div>
      {isError && (
        <MessageBar intent="error">
          <MessageBarBody>{apiErrorMessage(error, t, 'managementPage.sweepPreview.loadError')}</MessageBarBody>
        </MessageBar>
      )}
      {data && !isFetching && (
        <div className={styles.section} data-testid="sweep-preview-result">
          <div className={styles.row}>
            <Badge appearance="tint" color={data.flagEnabled ? 'success' : 'informative'}>
              {t(data.flagEnabled ? 'managementPage.sweepPreview.flagOn' : 'managementPage.sweepPreview.flagOff')}
            </Badge>
            <span className={styles.muted}>
              {t('managementPage.sweepPreview.counts', {
                cosmosCancel: data.cosmosOnly.cancel.length, cosmosReview: data.cosmosOnly.review.length,
                bothCancel: data.withDataverse.cancel.length, bothReview: data.withDataverse.review.length,
              })}
            </span>
          </div>
          {!data.deltaValid ? (
            <MessageBar intent="warning">
              <MessageBarBody>{t('managementPage.sweepPreview.deltaInvalid')}</MessageBarBody>
            </MessageBar>
          ) : data.newlyCancelled.length === 0 && data.noLongerCancelled.length === 0 ? (
            <MessageBar intent="success">
              <MessageBarBody data-testid="sweep-preview-empty">{t('managementPage.sweepPreview.noChange')}</MessageBarBody>
            </MessageBar>
          ) : (
            <>
              {data.newlyCancelled.length > 0 && (
                <>
                  <span className={styles.subTitle}>
                    {t('managementPage.sweepPreview.newlyCancelledTitle', { count: data.newlyCancelled.length })}
                  </span>
                  {itemsTable(data.newlyCancelled, 'newlyCancelled')}
                </>
              )}
              {data.noLongerCancelled.length > 0 && (
                <>
                  <span className={styles.subTitle}>
                    {t('managementPage.sweepPreview.noLongerCancelledTitle', { count: data.noLongerCancelled.length })}
                  </span>
                  {itemsTable(data.noLongerCancelled, 'noLongerCancelled')}
                </>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
